<!--
  BulkAttendeeStatusDrawer.vue
  이벤트 참여자 대량 생성 상태 조회 드로워
  
  - 대량 생성 진행 상태 표시
  - 실시간 진행률 업데이트
  - 생성 통계 및 설정 정보 표시
-->
<template>
  <a-drawer
    :visible="visible"
    :width="600"
    placement="right"
    :closable="true"
    :body-style="{ padding: '0' }"
    @close="handleClose"
  >
    <!-- 드로워 헤더 -->
    <template #title>
      <div class="drawer-header">
        <div class="header-content">
          <h3 class="drawer-title">참여자 생성 현황</h3>
          <div v-if="eventTitle" class="event-title-sub">{{ eventTitle }}</div>
        </div>
        <a-button 
          size="small" 
          type="default"
          @click="handleRefresh"
          :loading="loading"
          style="margin-left: 12px;"
        >
          <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i> 새로고침
        </a-button>
      </div>
    </template>

    <!-- 드로워 콘텐츠 -->
    <div class="drawer-content">
      <!-- 로딩 상태 -->
      <div v-if="loading && !statusData" class="loading-container">
        <a-spin size="large" tip="상태 정보를 불러오는 중..." />
      </div>

      <!-- 에러 상태 -->
      <div v-else-if="error" class="error-container">
        <a-alert
          message="조회 실패"
          :description="error"
          type="error"
          show-icon
        />
        <a-button type="primary" @click="fetchStatus" style="margin-top: 16px">
          다시 시도
        </a-button>
      </div>

      <!-- 상태 정보 표시 -->
      <div v-else-if="statusData" class="status-content">
        <!-- 상태 요약 섹션 -->
        <div class="status-summary-section">
          <div class="section-header">
            <h4>진행 상태</h4>
            <a-tag :color="getStatusColor(currentStatus)">
              {{ getStatusText(currentStatus) }}
            </a-tag>
          </div>
          
          <!-- 진행률 표시 -->
          <div class="progress-container">
            <a-progress
              :percent="progressPercentage"
              :status="getProgressStatus()"
              :stroke-color="getProgressColor()"
              :show-info="true"
            />
            <div class="progress-details">
              <span>{{ processedCount.toLocaleString() }} / {{ totalCount.toLocaleString() }}명 처리</span>
              <span v-if="failedCount > 0" class="failed-count">
                (실패: {{ failedCount.toLocaleString() }}명)
              </span>
            </div>
          </div>

          <!-- 시간 정보 -->
          <div class="time-info">
            <a-timeline>
              <a-timeline-item v-if="startedAt" color="green">
                <span class="time-label">시작:</span> {{ formatDateTime(startedAt) }}
              </a-timeline-item>
              <a-timeline-item v-if="completedAt" color="blue">
                <span class="time-label">완료:</span> {{ formatDateTime(completedAt) }}
                <span class="duration">(소요시간: {{ getDuration() }})</span>
              </a-timeline-item>
              <a-timeline-item v-else-if="isProcessing" color="gray">
                <span class="time-label">예상 완료:</span> {{ getEstimatedTime() }}
              </a-timeline-item>
            </a-timeline>
          </div>
        </div>

        <!-- 통계 섹션 -->
        <div class="statistics-section">
          <div class="section-header">
            <h4>처리 통계</h4>
          </div>
          <a-row :gutter="16">
            <a-col :span="8">
              <a-statistic
                title="전체 대상"
                :value="totalCount"
                :value-style="{ color: '#1890ff' }"
              >
                <template #prefix>
                  <i class="fa fa-users"></i>
                </template>
              </a-statistic>
            </a-col>
            <a-col :span="8">
              <a-statistic
                title="처리 완료"
                :value="processedCount - failedCount"
                :value-style="{ color: '#52c41a' }"
              >
                <template #prefix>
                  <i class="fa fa-check-circle"></i>
                </template>
              </a-statistic>
            </a-col>
            <a-col :span="8">
              <a-statistic
                title="처리 실패"
                :value="failedCount"
                :value-style="{ color: failedCount > 0 ? '#f5222d' : '#d9d9d9' }"
              >
                <template #prefix>
                  <i class="fa fa-exclamation-circle"></i>
                </template>
              </a-statistic>
            </a-col>
          </a-row>
        </div>

        <!-- 설정 정보 섹션 -->
        <div class="settings-section">
          <div class="section-header">
            <h4>생성 설정</h4>
          </div>
          <a-descriptions :column="1" size="small" bordered>
            <a-descriptions-item label="대상 유형">
              {{ getTargetTypeText(bulkSettings?.targetType) }}
            </a-descriptions-item>
            <a-descriptions-item label="배치 크기">
              {{ bulkSettings?.options?.batchSize?.toLocaleString() || '-' }}건
            </a-descriptions-item>
            <a-descriptions-item label="동시 처리">
              {{ bulkSettings?.options?.concurrency || '-' }}개
            </a-descriptions-item>
            <a-descriptions-item v-if="bulkSettings?.filters?.companyIds?.length" label="대상 회사">
              <div class="company-list">
                <a-tag v-for="id in bulkSettings.filters.companyIds" :key="id">
                  {{ id }}
                </a-tag>
              </div>
            </a-descriptions-item>
            <a-descriptions-item v-if="bulkSettings?.filters?.createdAfter" label="가입일 시작">
              {{ formatDateTime(bulkSettings.filters.createdAfter) }}
            </a-descriptions-item>
            <a-descriptions-item v-if="bulkSettings?.filters?.createdBefore" label="가입일 종료">
              {{ formatDateTime(bulkSettings.filters.createdBefore) }}
            </a-descriptions-item>
          </a-descriptions>
        </div>

        <!-- 작업 정보 섹션 (Bull Queue 정보) -->
        <div v-if="jobProgress" class="job-section">
          <div class="section-header">
            <h4>작업 정보</h4>
          </div>
          <a-descriptions :column="1" size="small" bordered>
            <a-descriptions-item label="작업 ID">
              {{ jobProgress.id || '-' }}
            </a-descriptions-item>
            <a-descriptions-item label="작업 상태">
              <a-tag :color="getJobStatusColor()">
                {{ getJobStatusText() }}
              </a-tag>
            </a-descriptions-item>
            <a-descriptions-item v-if="jobProgress.processedOn" label="처리 시작">
              {{ formatTimestamp(jobProgress.processedOn) }}
            </a-descriptions-item>
            <a-descriptions-item v-if="jobProgress.finishedOn" label="처리 완료">
              {{ formatTimestamp(jobProgress.finishedOn) }}
            </a-descriptions-item>
          </a-descriptions>
        </div>
      </div>
    </div>
  </a-drawer>
</template>

<script>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ko';

dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.locale('ko');

export default {
  name: 'BulkAttendeeStatusDrawer',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    eventId: {
      type: String,
      required: true
    },
    eventTitle: {
      type: String,
      default: ''
    },
    apiInstance: {
      type: Object,
      required: true
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const loading = ref(false);
    const error = ref(null);
    const statusData = ref(null);
    const refreshInterval = ref(null);

    // 상태 데이터 computed 속성들 - getStatusValue를 먼저 정의
    const getStatusValue = (key) => {
      const data = statusData.value?.data;
      if (!data) return null;
      
      // status._doc 우선 확인
      if (data.status?._doc?.[key] !== undefined) {
        return data.status._doc[key];
      }
      // status 직접 확인
      if (data.status?.[key] !== undefined) {
        return data.status[key];
      }
      return null;
    };
    
    const currentStatus = computed(() => {
      if (!statusData.value?.data) return 'idle';
      
      const processing = getStatusValue('processing');
      const completed = getStatusValue('completed');
      
      if (completed) return 'completed';
      if (processing) return 'processing';
      return 'idle';
    });

    const isProcessing = computed(() => currentStatus.value === 'processing');

    const totalCount = computed(() => getStatusValue('totalCount') || 0);
    const processedCount = computed(() => getStatusValue('processedCount') || 0);
    const failedCount = computed(() => getStatusValue('failedCount') || 0);
    const progressPercentage = computed(() => {
      // percentage가 있으면 사용, 없으면 계산
      const percentage = getStatusValue('percentage');
      if (percentage !== null) return percentage;
      
      const total = totalCount.value;
      const processed = processedCount.value;
      return total > 0 ? Math.round((processed / total) * 100) : 0;
    });

    const startedAt = computed(() => getStatusValue('startedAt'));
    const completedAt = computed(() => getStatusValue('completedAt'));

    const bulkSettings = computed(() => ({
      targetType: statusData.value?.data?.targetType || 'all',
      filters: statusData.value?.data?.filters || {},
      options: statusData.value?.data?.options || {}
    }));

    const jobProgress = computed(() => statusData.value?.data?.jobProgress);

    // 상태 조회 함수
    const fetchStatus = async () => {
      if (!props.eventId) return;
      
      loading.value = true;
      error.value = null;

      try {
        await new Promise((resolve, reject) => {
          props.apiInstance.getBulkStatus(
            props.eventId,
            (response) => {
              // response 구조 확인 후 데이터 저장
              if (response.data) {
                statusData.value = response.data;
              } else {
                statusData.value = response;
              }
              console.log('대량 생성 상태 조회 성공:', statusData.value);
              resolve(response);
            },
            (err) => {
              error.value = err.message || '상태 조회 중 오류가 발생했습니다.';
              reject(err);
            }
          );
        });
      } catch (err) {
        console.error('대량 생성 상태 조회 실패:', err);
      } finally {
        loading.value = false;
      }
    };

    // 자동 새로고침 제거 - 수동 새로고침만 사용
    const startAutoRefresh = () => {
      // 자동 새로고침 기능 비활성화
      console.log('자동 새로고침이 비활성화되었습니다.');
    };

    const stopAutoRefresh = () => {
      // 자동 새로고침 기능 비활성화
      if (refreshInterval.value) {
        clearInterval(refreshInterval.value);
        refreshInterval.value = null;
      }
    };

    // 수동 새로고침
    const handleRefresh = () => {
      fetchStatus();
    };

    // 드로워 닫기
    const handleClose = () => {
      stopAutoRefresh();
      emit('close');
    };

    // 헬퍼 함수들
    const getStatusColor = (status) => {
      switch (status) {
        case 'processing': return 'processing';
        case 'completed': return 'success';
        case 'failed': return 'error';
        default: return 'default';
      }
    };

    const getStatusText = (status) => {
      switch (status) {
        case 'processing': return '처리 중';
        case 'completed': return '완료';
        case 'failed': return '실패';
        default: return '대기';
      }
    };

    const getProgressStatus = () => {
      if (currentStatus.value === 'completed') return 'success';
      if (failedCount.value > 0) return 'exception';
      if (isProcessing.value) return 'active';
      return 'normal';
    };

    const getProgressColor = () => {
      if (failedCount.value > 0) return '#ff4d4f';
      if (currentStatus.value === 'completed') return '#52c41a';
      return '#1890ff';
    };

    const getTargetTypeText = (type) => {
      switch (type) {
        case 'all': return '모든 고객 (블랙리스트 제외)';
        case 'nft_holders': return 'NFT/인증서 보유 고객';
        case 'specific': return '특정 조건 지정';
        default: return type || '-';
      }
    };

    const formatDateTime = (dateStr) => {
      if (!dateStr) return '-';
      return dayjs(dateStr).format('YYYY-MM-DD HH:mm:ss');
    };

    const formatTimestamp = (timestamp) => {
      if (!timestamp) return '-';
      return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
    };

    const getDuration = () => {
      if (!startedAt.value || !completedAt.value) return '-';
      
      const start = dayjs(startedAt.value);
      const end = dayjs(completedAt.value);
      const diff = end.diff(start);
      const duration = dayjs.duration(diff);
      
      if (duration.hours() > 0) {
        return `${duration.hours()}시간 ${duration.minutes()}분 ${duration.seconds()}초`;
      } else if (duration.minutes() > 0) {
        return `${duration.minutes()}분 ${duration.seconds()}초`;
      } else {
        return `${duration.seconds()}초`;
      }
    };

    const getEstimatedTime = () => {
      if (!isProcessing.value || processedCount.value === 0) return '-';
      
      const processed = processedCount.value;
      const total = totalCount.value;
      const elapsed = dayjs().diff(dayjs(startedAt.value), 'second');
      const rate = processed / elapsed; // 초당 처리 건수
      const remaining = total - processed;
      const estimatedSeconds = remaining / rate;
      
      return dayjs().add(estimatedSeconds, 'second').format('HH:mm:ss');
    };

    const getJobStatusColor = () => {
      if (!jobProgress.value) return 'default';
      if (jobProgress.value.isCompleted) return 'success';
      if (jobProgress.value.isFailed) return 'error';
      if (jobProgress.value.isActive) return 'processing';
      if (jobProgress.value.isWaiting) return 'warning';
      return 'default';
    };

    const getJobStatusText = () => {
      if (!jobProgress.value) return '-';
      if (jobProgress.value.isCompleted) return '완료';
      if (jobProgress.value.isFailed) return '실패';
      if (jobProgress.value.isActive) return '처리 중';
      if (jobProgress.value.isWaiting) return '대기 중';
      return '알 수 없음';
    };

    // Watch visible prop
    watch(() => props.visible, (newVal) => {
      if (newVal) {
        fetchStatus();
        // 자동 새로고침 비활성화 - 수동 새로고침만 사용
      } else {
        stopAutoRefresh();
      }
    });

    // Cleanup
    onBeforeUnmount(() => {
      stopAutoRefresh();
    });

    return {
      loading,
      error,
      statusData,
      currentStatus,
      isProcessing,
      totalCount,
      processedCount,
      failedCount,
      progressPercentage,
      startedAt,
      completedAt,
      bulkSettings,
      jobProgress,
      
      fetchStatus,
      handleRefresh,
      handleClose,
      
      getStatusColor,
      getStatusText,
      getProgressStatus,
      getProgressColor,
      getTargetTypeText,
      formatDateTime,
      formatTimestamp,
      getDuration,
      getEstimatedTime,
      getJobStatusColor,
      getJobStatusText
    };
  }
};
</script>

<style scoped lang="scss">
.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  
  .header-content {
    flex: 1;
  }
  
  .drawer-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }
  
  .event-title-sub {
    margin-top: 4px;
    font-size: 13px;
    color: #666;
    font-weight: normal;
  }
}

.drawer-content {
  padding: 20px;
  height: calc(100vh - 55px);
  overflow-y: auto;
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  text-align: center;
}

.status-content {
  > div {
    margin-bottom: 24px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  
  h4 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #333;
  }
}

// 상태 요약 섹션
.status-summary-section {
  background: #f9f9f9;
  padding: 16px;
  border-radius: 8px;
  
  .progress-container {
    margin: 16px 0;
    
    .progress-details {
      margin-top: 8px;
      font-size: 13px;
      color: #666;
      display: flex;
      justify-content: space-between;
      align-items: center;
      
      .failed-count {
        color: #ff4d4f;
        font-weight: 500;
      }
    }
  }
  
  .time-info {
    margin-top: 16px;
    
    .time-label {
      font-weight: 500;
      margin-right: 4px;
    }
    
    .duration {
      margin-left: 8px;
      color: #666;
      font-size: 12px;
    }
    
    :deep(.ant-timeline) {
      margin-top: 12px;
    }
    
    :deep(.ant-timeline-item) {
      padding-bottom: 12px;
      
      &:last-child {
        padding-bottom: 0;
      }
    }
    
    :deep(.ant-timeline-item-content) {
      font-size: 13px;
    }
  }
}

// 통계 섹션
.statistics-section {
  :deep(.ant-statistic) {
    text-align: center;
  }
  
  :deep(.ant-statistic-title) {
    font-size: 13px;
    color: #666;
  }
  
  :deep(.ant-statistic-content) {
    font-size: 20px;
  }
  
  :deep(.ant-statistic-content-prefix) {
    margin-right: 4px;
    font-size: 16px;
  }
}

// 설정 정보 섹션
.settings-section,
.job-section {
  :deep(.ant-descriptions) {
    background: #fff;
  }
  
  :deep(.ant-descriptions-item-label) {
    font-weight: 500;
    color: #666;
    background: #fafafa;
  }
  
  :deep(.ant-descriptions-item-content) {
    color: #333;
  }
  
  .company-list {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    
    .ant-tag {
      margin: 0;
    }
  }
}

// 반응형 디자인
@media (max-width: 768px) {
  .drawer-content {
    padding: 16px;
  }
  
  :deep(.ant-drawer) {
    width: 100% !important;
    max-width: 100% !important;
  }
}
</style>